/**
 * **높이맵 지형** (2026-09-10, Chris: 「게리모드 툴 건처럼 지형 올리고 꺼지게」)
 *
 * 밖 땅을 사각형 판 대신 1m 격자 높이값으로 둔다. 시작값은 기존 판·경사로가 주던 높이 그대로라
 * 아무것도 안 만지면 예전과 같다. 건물 발판(BUILDINGS·ROOMS) 안 셀은 **잠금** — 붓이 못 건드려 실내 바닥이 평평하게 남는다.
 * 편집은 셀별 변화량(delta)만 저장·적용한다(오버라이드 층). 발밑 높이(ground.ts)와 땅 메시(outside.ts·에디터)가 같은 격자를 읽는다.
 */
import * as THREE from 'three';
import type { LevelDef } from '../../shared/level-types';
import type { OutdoorDef } from '../../shared/outdoor';

/**
 * **밖 지형의 실제 높이.** 게임의 언덕은 `OUTDOOR.mounds`(고원 + 축대 비탈)와 `causeways`(경사로)로 **그려지는데**,
 * 걷는 높이는 따로 `slabs`·`ramps` 였다. 둘이 다른 모양이라 에디터에 높이맵을 깔았더니 그림과 어긋났다
 * (Chris 2026-09-10: 「지형 전체가 실제 인게임이랑 다르잖아」). 그래서 **그려지는 모양 그대로** 높이를 계산한다.
 * - 고원 사각형 안: 그 높이
 * - 밖으로 batter(m) 안: 거리에 따라 선형으로 0 까지 내려가는 축대 비탈
 * - `gapS`: 남쪽 면의 뚫린 구간 — 거기는 비탈 대신 경사로가 채운다
 * - causeway: zLow(0) → zHigh(yHigh) 로 오르는 길, 양옆으로 batter 만큼 비탈
 */
export function outdoorHeight(o: OutdoorDef, x: number, z: number, base: number): number {
  let y = base;
  for (const m of o.mounds ?? []) {
    const inX = x >= m.x1 && x <= m.x2, inZ = z >= m.z1 && z <= m.z2;
    if (inX && inZ) { y = Math.max(y, m.y); continue; }
    const b = m.batter ?? 4;
    // 사각형까지의 바깥 거리
    const dx = x < m.x1 ? m.x1 - x : x > m.x2 ? x - m.x2 : 0;
    const dz = z < m.z1 ? m.z1 - z : z > m.z2 ? z - m.z2 : 0;
    const d = Math.hypot(dx, dz);
    if (d >= b) continue;
    // 남쪽(z2) 면의 뚫린 구간은 비탈이 없다 — 경사로가 지난다
    if (m.gapS && z > m.z2 && x >= m.gapS[0] && x <= m.gapS[1]) continue;
    y = Math.max(y, m.y * (1 - d / b));
  }
  for (const c of o.causeways ?? []) {
    const zLo = Math.min(c.zLow, c.zHigh), zHi = Math.max(c.zLow, c.zHigh);
    if (z < zLo || z > zHi) continue;
    const t = (z - c.zLow) / (c.zHigh - c.zLow);
    const top = c.yHigh * Math.min(1, Math.max(0, t));
    const b = c.batter ?? 2.6;
    const dx = x < c.x1 ? c.x1 - x : x > c.x2 ? x - c.x2 : 0;
    if (dx >= b) continue;
    y = Math.max(y, top * (1 - dx / b));
  }
  return y;
}

export interface TerrainDeltas { cell: number; x0: number; z0: number; w: number; h: number; d: Record<string, number> }

export class Terrain {
  readonly cell: number;
  readonly x0: number; readonly z0: number; readonly w: number; readonly h: number;   // w·h = 셀 수
  readonly H: Float32Array;        // 현재 높이
  readonly base: Float32Array;     // 시작 높이(판·경사로)
  readonly locked: Uint8Array;     // 1 = 건물 발판, 붓 금지
  private mesh: THREE.Mesh | null = null;
  /** buildMesh 가 만든 땅 메시 — 에디터가 붓 레이캐스트 대상으로 쓴다 */
  get object(): THREE.Mesh | null { return this.mesh; }

  constructor(level: LevelDef, baseAt: (x: number, z: number) => number, lockRects: { x1: number; z1: number; x2: number; z2: number }[], cell = 1) {
    this.cell = cell;
    const b = level.bounds;
    this.x0 = Math.floor(b.x1); this.z0 = Math.floor(b.z1);
    this.w = Math.ceil((b.x2 - this.x0) / cell) + 1; this.h = Math.ceil((b.z2 - this.z0) / cell) + 1;
    this.H = new Float32Array(this.w * this.h); this.base = new Float32Array(this.w * this.h); this.locked = new Uint8Array(this.w * this.h);
    for (let j = 0; j < this.h; j++) for (let i = 0; i < this.w; i++) {
      const x = this.x0 + i * cell, z = this.z0 + j * cell; const k = j * this.w + i;
      this.base[k] = this.H[k] = baseAt(x, z);
      for (const r of lockRects) if (x >= r.x1 - 0.5 && x <= r.x2 + 0.5 && z >= r.z1 - 0.5 && z <= r.z2 + 0.5) { this.locked[k] = 1; break; }
    }
  }
  private idx(i: number, j: number) { return Math.min(this.h - 1, Math.max(0, j)) * this.w + Math.min(this.w - 1, Math.max(0, i)); }
  /** 격자 보간 높이. 밖이면 가장자리 값 */
  sample(x: number, z: number): number {
    const fx = (x - this.x0) / this.cell, fz = (z - this.z0) / this.cell;
    const i = Math.floor(fx), j = Math.floor(fz); const tx = Math.min(1, Math.max(0, fx - i)), tz = Math.min(1, Math.max(0, fz - j));
    const a = this.H[this.idx(i, j)], b = this.H[this.idx(i + 1, j)], c = this.H[this.idx(i, j + 1)], d = this.H[this.idx(i + 1, j + 1)];
    return (a * (1 - tx) + b * tx) * (1 - tz) + (c * (1 - tx) + d * tx) * tz;
  }
  /** 붓 한 번. mode: raise|lower|flatten|smooth. 반환 = 바뀐 셀의 이전 값(되돌리기용) */
  brush(x: number, z: number, radius: number, strength: number, mode: 'raise' | 'lower' | 'flatten' | 'smooth', flatTo?: number): Map<number, number> {
    const before = new Map<number, number>();
    const i0 = Math.floor((x - radius - this.x0) / this.cell), i1 = Math.ceil((x + radius - this.x0) / this.cell);
    const j0 = Math.floor((z - radius - this.z0) / this.cell), j1 = Math.ceil((z + radius - this.z0) / this.cell);
    const target = flatTo ?? this.sample(x, z);
    for (let j = Math.max(0, j0); j <= Math.min(this.h - 1, j1); j++) for (let i = Math.max(0, i0); i <= Math.min(this.w - 1, i1); i++) {
      const k = j * this.w + i; if (this.locked[k]) continue;
      const dx = this.x0 + i * this.cell - x, dz = this.z0 + j * this.cell - z; const r = Math.hypot(dx, dz) / radius; if (r > 1) continue;
      const f = 1 - r * r * (3 - 2 * r);   // smoothstep 감쇠 — 가운데 1, 가장자리 0
      const cur = this.H[k]; let next = cur;
      if (mode === 'raise') next = cur + strength * f;
      else if (mode === 'lower') next = cur - strength * f;
      else if (mode === 'flatten') next = cur + (target - cur) * Math.min(1, f * 1.5);
      else { // smooth — 이웃 평균으로
        const n = (this.H[this.idx(i - 1, j)] + this.H[this.idx(i + 1, j)] + this.H[this.idx(i, j - 1)] + this.H[this.idx(i, j + 1)]) / 4;
        next = cur + (n - cur) * Math.min(1, f);
      }
      if (next !== cur) { before.set(k, cur); this.H[k] = next; }
    }
    return before;
  }
  restore(cells: Map<number, number>) { for (const [k, v] of cells) this.H[k] = v; }
  /** 시작값과 다른 셀만 — 저장 */
  deltas(): TerrainDeltas {
    const d: Record<string, number> = {};
    for (let k = 0; k < this.H.length; k++) { const dv = this.H[k] - this.base[k]; if (Math.abs(dv) > 1e-4) d[k] = +dv.toFixed(4); }
    return { cell: this.cell, x0: this.x0, z0: this.z0, w: this.w, h: this.h, d };
  }
  applyDeltas(t?: TerrainDeltas | null) {
    if (!t || t.cell !== this.cell || t.x0 !== this.x0 || t.z0 !== this.z0 || t.w !== this.w) return 0;
    let n = 0; for (const [k, dv] of Object.entries(t.d)) { const i = +k; if (i >= 0 && i < this.H.length) { this.H[i] = this.base[i] + dv; n++; } }
    return n;
  }
  /** 절벽: 이웃 셀과 높이차가 step 보다 큰 셀 — 걸어서 못 넘는 자리 */
  cliffs(step: number): { x: number; z: number; dh: number }[] {
    const out: { x: number; z: number; dh: number }[] = [];
    for (let j = 0; j < this.h - 1; j++) for (let i = 0; i < this.w - 1; i++) {
      const k = j * this.w + i; const a = this.H[k];
      const dh = Math.max(Math.abs(a - this.H[k + 1]), Math.abs(a - this.H[k + this.w]));
      if (dh > step && !this.locked[k]) out.push({ x: this.x0 + i * this.cell, z: this.z0 + j * this.cell, dh });
    }
    return out;
  }
  /** 땅 메시 — 격자 그대로 판을 깔고 정점 y 를 높이로. `refresh()` 로 붓질 뒤 갱신 */
  buildMesh(material: THREE.Material, yOffset = -0.02): THREE.Mesh {
    const g = new THREE.PlaneGeometry((this.w - 1) * this.cell, (this.h - 1) * this.cell, this.w - 1, this.h - 1);
    g.rotateX(-Math.PI / 2);
    g.translate(this.x0 + ((this.w - 1) * this.cell) / 2, 0, this.z0 + ((this.h - 1) * this.cell) / 2);
    this.mesh = new THREE.Mesh(g, material);
    this.mesh.name = 'terrain';
    this.mesh.userData.yOffset = yOffset;
    this.refresh();
    return this.mesh;
  }
  /** 에디터용 — 높이로 색을 입힌다. 단색 회색이면 1.4m 언덕도 안 보인다(2026-09-10 컷).
   * 낮은 곳 파랑기 → 기준 회색 → 높은 곳 흰색, 잠긴 셀(건물 발판)은 붉은기 */
  /** 높이 색을 끈다 — 게임과 같은 눈 재질로 돌아간다 */
  clearHeightPaint() {
    if (!this.mesh) return;
    this.mesh.geometry.deleteAttribute('color');
    const m = this.mesh.material as THREE.MeshStandardMaterial;
    m.vertexColors = false; m.needsUpdate = true;
  }
  paintByHeight(lo = -2, hi = 5) {
    if (!this.mesh) return;
    const g = this.mesh.geometry; const n = this.w * this.h;
    const col = new Float32Array(n * 3);
    for (let k = 0; k < n; k++) {
      const t = Math.min(1, Math.max(0, (this.H[k] - lo) / (hi - lo)));
      let r = 0.42 + t * 0.55, gg = 0.5 + t * 0.47, bb = 0.62 + t * 0.36;
      if (this.locked[k]) { r = Math.min(1, r + 0.22); gg *= 0.82; bb *= 0.78; }
      col[k * 3] = r; col[k * 3 + 1] = gg; col[k * 3 + 2] = bb;
    }
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    (this.mesh.material as THREE.MeshStandardMaterial).vertexColors = true;
    (this.mesh.material as THREE.MeshStandardMaterial).needsUpdate = true;
  }
  refresh() {
    if (!this.mesh) return;
    const pos = this.mesh.geometry.attributes.position as THREE.BufferAttribute; const yo = this.mesh.userData.yOffset as number;
    // PlaneGeometry 는 위(−z)→아래(+z) 행 순서 — rotateX(−π/2) 뒤 첫 행이 z 작은 쪽
    for (let j = 0; j < this.h; j++) for (let i = 0; i < this.w; i++) pos.setY(j * this.w + i, this.H[j * this.w + i] + yo);
    pos.needsUpdate = true; this.mesh.geometry.computeVertexNormals(); this.mesh.geometry.computeBoundingSphere();
    if (this.mesh.geometry.getAttribute('color')) this.paintByHeight();
  }
}

/** 게임·에디터가 같이 쓰는 한 개. main/place-main 이 만들어 둔다 */
let current: Terrain | null = null;
export const setTerrain = (t: Terrain | null) => { current = t; };
export const getTerrain = () => current;
